import { defineStore } from 'pinia'
import { computed, ref, unref } from 'vue'
import type {
  JobFilter,
  JobFilterComparison,
  JobFilterSet,
  JobFilterType,
  JobListSort,
} from '@ironsight/types'
import { ApprovalStatus, JobStatus } from '@/models/job'
import { intersection, map, parseInt } from 'lodash'
import { mergeInterval } from '@/utils/dateUtils'
import moment from 'moment'
import {
  BUCKET_DATE_FILTER_PERSISTENCE_TTL_MS,
  BucketDefinition,
  getBucketGroupForBucketId,
  getBucketDateFilterId,
  getBucketIdFromDateFilterId,
  isBucketDateFilter,
  isBucketManagedProperty,
  isBucketStatusFilter,
} from '@/components/jobs/job-search-page/useJobFilterBuckets'
import { getDefaultDateRangeForBucket } from '@/components/jobs/job-search-page/bucketDateRangeUtils'
import { ITEMS_PER_PAGE_OPTIONS, ItemsPerPageOptions } from './useJobListPagination'

/**
 * Adapter for FilteredJobStore for Components using Legacy Filter Formats.
 */
export function useFilteredJobStoreLegacyAdapter() {
  const filterStore = useFilteredJobStore()

  const DIVISION_KEY = 'OwnerId'
  const PRIORITY_KEY = 'Priority'
  const RESOURCETYPE_KEY = 'ResourceTypeId'
  const SERVICEPROVIDER_KEY = 'ServiceProviderId'
  const STATUS_KEY = 'Status'
  const ACTIVITY_KEY = 'TaskId'
  const LOCATION_KEY = 'LocationId'
  const START_KEY = 'Start'
  const END_KEY = 'End'
  const COMPLETED_TIME_KEY = 'CompletedTime'
  const START_TIME_KEY = 'StartTime'
  const jobStatuses = Object.values(JobStatus)
  const approvalStatuses = Object.values(ApprovalStatus)

  const selectedPriorities = computed({
    get: () => toLegacyFilters(PRIORITY_KEY).map((x) => parseInt(x, 10)),
    set: (values: number[]) => {
      fromLegacyFilters(PRIORITY_KEY, 'IntLookup', 'In', values)
    },
  })

  const selectedDivisions = computed({
    get: () => toLegacyFilters(DIVISION_KEY),
    set: (values: string[]) => fromLegacyFilters(DIVISION_KEY, 'StringLookup', 'In', values),
  })

  const selectedServiceProviders = computed({
    get: () => toLegacyFilters(SERVICEPROVIDER_KEY),
    set: (values: any[]) => {
      if (values[0] === null) values[0] = ''
      return fromLegacyFilters(SERVICEPROVIDER_KEY, 'StringLookup', 'In', values)
    },
  })

  const selectedResourceTypes = computed({
    get: () => toLegacyFilters(RESOURCETYPE_KEY),
    set: (values: string[]) => fromLegacyFilters(RESOURCETYPE_KEY, 'IntLookup', 'In', values),
  })

  const selectedJobStatuses = computed({
    get: () => {
      return intersection(toLegacyFilters(STATUS_KEY), jobStatuses)
    },
    set: (values: string[]) => fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', values),
  })

  const selectedApprovalStatuses = computed({
    get: () => {
      return intersection(toLegacyFilters(STATUS_KEY), approvalStatuses)
    },
    set: (values: string[]) => fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', values),
  })

  const selectedActivities = computed({
    get: () => toLegacyFilters(ACTIVITY_KEY),
    set: (values: string[]) => fromLegacyFilters(ACTIVITY_KEY, 'StringLookup', 'In', values),
  })
  const selectedLocations = computed<(number | null)[]>({
    get: () =>
      toLegacyFilters(LOCATION_KEY).map((x) => {
        const parsed = parseInt(x, 10)
        return isNaN(parsed) ? null : parsed
      }),
    set: (values: (number | null)[]) => fromLegacyFilters(LOCATION_KEY, 'IntLookup', 'In', values),
  })

  const selectedStart = computed<moment.Moment | null>(() => {
    const filter = toLegacyFilters(START_KEY)
    if (filter.length === 1) {
      return moment(filter[0])
    }
    return null
  })

  const selectedEnd = computed<moment.Moment | null>(() => {
    const filter = toLegacyFilters(END_KEY)
    if (filter.length === 1) {
      return moment(filter[0])
    }
    return null
  })

  const updateDateRange = (start: moment.Moment, end: moment.Moment) => {
    fromLegacyFilters(COMPLETED_TIME_KEY, 'DateTime', 'Between', [start, end])
    fromLegacyFilters(START_TIME_KEY, 'DateTime', 'Between', [start, end])
  }

  const clearDateRange = () => {
    fromLegacyFilters(COMPLETED_TIME_KEY, 'DateTime', 'Between', [])
    fromLegacyFilters(START_TIME_KEY, 'DateTime', 'Between', [])
  }

  const hasDateRange = computed(() => {
    return (
      toLegacyFilters(COMPLETED_TIME_KEY).length > 0 || toLegacyFilters(START_TIME_KEY).length > 0
    )
  })

  const dateRangeLabel = computed(() => {
    return hasDateRange.value
      ? `${selectedEnd.value!.diff(selectedStart.value!, 'days') + 1} dates selected`
      : 'Showing all dates'
  })

  /**
   * Returns the values in Legacy format.
   *
   * @param key : string
   *  The property name of the filter.
   * @returns string[]
   *  The array of values
   */
  const toLegacyFilters = (key: string): string[] => {
    const filters = filterStore.getFilters
    if (key === START_KEY || key === END_KEY) {
      const completedTime = filters.find((x) => x.property === COMPLETED_TIME_KEY)
      const startTime = filters.find((x) => x.property === START_TIME_KEY)
      const mergedInterval = mergeInterval(startTime?.value, completedTime?.value)
      if (mergedInterval.length === 2) {
        const index = key === START_KEY ? 0 : 1
        return [mergedInterval[index]]
      }
      return []
    }
    const idx = filters.findIndex((x) => x.property === key)
    if (idx === -1) return []

    if (filters[idx].value?.[0]?.value !== undefined) {
      // if the value is 'null' or '' then map it to null
      return map(filters[idx].value, (item) => {
        const v = item.value
        return v === 'null' || v === '' ? null : v
      })
    }

    return filters[idx].value
  }

  /**
   * Sets the values in Legacy format to the JobFilters (`JobFilters.ts`).
   *
   * @param key : string
   *  The property name of the filter.
   * @param filterType : JobFilterType
   *  The type of filter.
   * @param comparisonType : JobFilterComparison
   *  The type of comparison.
   *  @param values : any[]
   *  The array of values
   *  @param hidden : boolean | undefined
   *  Whether the filter is shown in the UI
   * @returns string[]
   *  The array of values
   */
  const fromLegacyFilters = (
    key: string,
    filterType: JobFilterType,
    comparisonType: JobFilterComparison,
    values: any[],
    hidden?: boolean,
  ) => {
    const filters = filterStore.getFilters
    const idx = filters.findIndex((x) => x.property === key)
    if (values.length === 0 && idx !== -1) {
      filterStore.removeFilter(filters[idx].id)
    } else if (values.length > 0) {
      if (key === PRIORITY_KEY) {
        values = values.map((v) => parseInt(unref(v), 10))
      }
      filterStore.setFilter({
        id: idx !== -1 ? filters[idx].id : crypto.randomUUID(),
        property: key,
        value: values,
        type: filterType,
        comparison: comparisonType,
        hidden,
      })
    }
  }

  function $patch(payload: Record<string, any>, hidden?: boolean) {
    if ('selectedPriorities' in payload && selectedPriorities) {
      fromLegacyFilters(PRIORITY_KEY, 'IntLookup', 'In', payload.selectedPriorities, hidden)
    }

    if ('selectedDivisions' in payload && selectedDivisions) {
      fromLegacyFilters(DIVISION_KEY, 'StringLookup', 'In', payload.selectedDivisions, hidden)
    }

    if ('selectedServiceProviders' in payload && selectedServiceProviders) {
      fromLegacyFilters(
        SERVICEPROVIDER_KEY,
        'StringLookup',
        'In',
        payload.selectedServiceProviders,
        hidden,
      )
    }

    if ('selectedResourceTypes' in payload && selectedResourceTypes) {
      fromLegacyFilters(RESOURCETYPE_KEY, 'IntLookup', 'In', payload.selectedResourceTypes, hidden)
    }

    if ('selectedActivities' in payload && selectedActivities) {
      fromLegacyFilters(ACTIVITY_KEY, 'StringLookup', 'In', payload.selectedActivities, hidden)
    }

    if ('selectedJobStatuses' in payload && selectedJobStatuses) {
      fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', payload.selectedJobStatuses, hidden)
    }

    if ('selectedApprovalStatuses' in payload && selectedApprovalStatuses) {
      fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', payload.selectedApprovalStatuses, hidden)
    }

    if ('selectedLocations' in payload && selectedLocations) {
      fromLegacyFilters(LOCATION_KEY, 'IntLookup', 'In', payload.selectedLocations, hidden)
    }
  }

  return {
    selectedPriorities,
    selectedDivisions,
    selectedServiceProviders,
    selectedResourceTypes,
    selectedJobStatuses,
    selectedApprovalStatuses,
    selectedActivities,
    selectedLocations,
    selectedStart,
    selectedEnd,
    updateDateRange,
    clearDateRange,
    hasDateRange,
    dateRangeLabel,
    $patch,
  }
}

function normalizeAndEvictBucketDateFiltersInPlace(filters: JobFilter[]) {
  if (!filters.length) {
    return filters
  }

  const now = Date.now()
  let writeIndex = 0
  for (let readIndex = 0; readIndex < filters.length; readIndex += 1) {
    const filter = filters[readIndex]

    if (isBucketDateFilter(filter) && filter.type === 'DateTime') {
      let expiresAt = filter.expiresAt

      if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) {
        expiresAt = now + BUCKET_DATE_FILTER_PERSISTENCE_TTL_MS
        filter.expiresAt = expiresAt
      }

      if (expiresAt <= now) {
        continue
      }
    }

    if (filter.type === 'DateTime' && Array.isArray(filter.value)) {
      for (let i = 0; i < filter.value.length; i += 1) {
        const dateValue = filter.value[i]
        if (typeof dateValue === 'string') {
          filter.value[i] = new Date(dateValue)
        }
      }
    }

    filters[writeIndex] = filter
    writeIndex += 1
  }

  filters.length = writeIndex
  return filters
}

function normalizeActiveDatePresetForBucketGroupMap(
  activePresetMap: unknown,
  filters: JobFilter[],
): Record<string, string | null> {
  if (!activePresetMap || typeof activePresetMap !== 'object' || Array.isArray(activePresetMap)) {
    return {}
  }

  const activeBucketIds = new Set(
    filters
      .filter((filter) => isBucketDateFilter(filter) && filter.type === 'DateTime')
      .map((filter) => getBucketIdFromDateFilterId(filter.id))
      .filter((bucketId): bucketId is string => Boolean(bucketId)),
  )
  const normalized: Record<string, string | null> = {}

  for (const [bucketId, presetKey] of Object.entries(activePresetMap)) {
    if (!activeBucketIds.has(bucketId)) {
      continue
    }

    normalized[bucketId] = typeof presetKey === 'string' ? presetKey : null
  }

  return normalized
}

/**
 * If the selected bucket requires a date filter and it was evicted during
 * restore (default filters use an immediate expiry), re-add it with the
 * bucket's default preset so that queries never fire without a date range.
 */
function restoreEvictedDefaultDateFilter(bucket: BucketDefinition | null, filters: JobFilter[]) {
  if (!bucket?.dateRangeFilter) return

  const dateFilterId = getBucketDateFilterId(bucket.id)
  const hasDateFilter = filters.some((f) => f.id === dateFilterId)
  if (hasDateFilter) return

  const defaultRange = getDefaultDateRangeForBucket(bucket)
  if (!defaultRange) return

  filters.push({
    id: dateFilterId,
    property: bucket.dateRangeFilter.property,
    type: 'DateTime',
    comparison: 'Between',
    value: defaultRange,
    hidden: true,
    expiresAt: Date.now(),
  })
}

function normalizeItemsPerPage(
  value: unknown,
  fallback: ItemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS[0],
): ItemsPerPageOptions {
  return ITEMS_PER_PAGE_OPTIONS.includes(value as ItemsPerPageOptions)
    ? (value as ItemsPerPageOptions)
    : fallback
}

const BUCKET_GROUP_SORT_KEY_PREFIX = 'group:'

function getSortSelectionKeyForBucket(bucket: Pick<BucketDefinition, 'group'>) {
  return `${BUCKET_GROUP_SORT_KEY_PREFIX}${bucket.group}`
}

function isPersistedJobListSort(value: unknown): value is JobListSort | null {
  if (value === null) {
    return true
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const sort = value as Partial<JobListSort>
  return typeof sort.field === 'string' && (sort.direction === 'asc' || sort.direction === 'desc')
}

function normalizeBucketSortSelections(
  persistedSelections: unknown,
  selectedBucket: BucketDefinition | null | undefined,
): Partial<Record<string, JobListSort | null>> {
  if (
    !persistedSelections ||
    typeof persistedSelections !== 'object' ||
    Array.isArray(persistedSelections)
  ) {
    return {}
  }

  const rawSelections = persistedSelections as Record<string, unknown>
  const normalizedSelections: Partial<Record<string, JobListSort | null>> = {}
  const selectedBucketGroupKey = selectedBucket
    ? getSortSelectionKeyForBucket(selectedBucket)
    : undefined
  const selectedBucketLegacyValue =
    selectedBucket && isPersistedJobListSort(rawSelections[selectedBucket.id])
      ? (rawSelections[selectedBucket.id] as JobListSort | null)
      : undefined

  if (selectedBucketGroupKey && selectedBucketLegacyValue !== undefined) {
    normalizedSelections[selectedBucketGroupKey] = selectedBucketLegacyValue
  }

  for (const [key, value] of Object.entries(rawSelections)) {
    if (!key.startsWith(BUCKET_GROUP_SORT_KEY_PREFIX) || !isPersistedJobListSort(value)) {
      continue
    }

    normalizedSelections[key] = value
  }

  for (const [key, value] of Object.entries(rawSelections)) {
    if (key.startsWith(BUCKET_GROUP_SORT_KEY_PREFIX) || !isPersistedJobListSort(value)) {
      continue
    }

    const bucketGroup = getBucketGroupForBucketId(key)
    if (bucketGroup === null) {
      continue
    }

    const groupKey = `${BUCKET_GROUP_SORT_KEY_PREFIX}${bucketGroup}`
    if (normalizedSelections[groupKey] === undefined) {
      normalizedSelections[groupKey] = value
    }
  }

  return normalizedSelections
}

export const useFilteredJobStore = defineStore(
  'filtered-jobs',
  () => {
    const filters = ref<JobFilter[]>([])
    const pendingFilter = ref<JobFilter | undefined>(undefined)
    const selectedFilterSetId = ref<string | null>(null)
    const activeDatePresetForBucketGroupMap = ref<Record<string, string | null>>({})

    const DEFAULT_ITEMS_PER_PAGE = 20
    const DEFAULT_SORT: JobListSort = {
      field: 'Created',
      direction: 'desc',
    }

    const selectedBucket = ref<BucketDefinition | null>(null)

    const isBucketsSidePanelCollapsed = ref<boolean>(false)
    const toggleBucketsSidePanel = () => {
      isBucketsSidePanelCollapsed.value = !isBucketsSidePanelCollapsed.value
    }

    const bucketSortSelections = ref<Partial<Record<string, JobListSort | null>>>({})

    const getSortForBucket = (bucket: Pick<BucketDefinition, 'group' | 'defaultSort'>) => {
      return (
        bucketSortSelections.value[getSortSelectionKeyForBucket(bucket)] ??
        bucket.defaultSort ??
        DEFAULT_SORT
      )
    }

    const getSortForSelectedBucket = computed(() => {
      if (!selectedBucket.value) return DEFAULT_SORT
      return getSortForBucket(selectedBucket.value)
    })

    const setSortForBucket = (bucket: BucketDefinition, sort: JobListSort) => {
      const key = getSortSelectionKeyForBucket(bucket)
      bucketSortSelections.value = {
        ...bucketSortSelections.value,
        [key]: sort,
      }
    }

    const clearSortForBucket = (bucket: Pick<BucketDefinition, 'group'>) => {
      const key = getSortSelectionKeyForBucket(bucket)
      const remainingSelections = { ...bucketSortSelections.value }
      delete remainingSelections[key]
      bucketSortSelections.value = remainingSelections
    }

    function updateSorting(newSorting: JobListSort) {
      if (selectedBucket.value) {
        setSortForBucket(selectedBucket.value, newSorting)
      }
    }

    const currentPage = ref<number>(1)
    const itemsPerPage = ref<number>(DEFAULT_ITEMS_PER_PAGE)

    const filtersWithoutBuckets = computed(() =>
      filters.value.filter(
        (filter) => !isBucketDateFilter(filter) && !isBucketStatusFilter(filter),
      ),
    )

    const excludeBucketManagedFilters = (filtersToFilter: JobFilter[]): JobFilter[] => {
      return filtersToFilter.filter(
        (filter) =>
          isBucketDateFilter(filter) ||
          isBucketStatusFilter(filter) ||
          !isBucketManagedProperty(filter),
      )
    }

    const getFilters = computed(() => {
      return excludeBucketManagedFilters(filters.value)
    })

    const getVisibleFilters = computed(() => {
      let visible: JobFilter[]

      if (pendingFilter.value !== undefined) {
        const idx = filters.value.findIndex((f) => f.id === pendingFilter.value!.id)
        if (idx !== -1) {
          const updated = [...filters.value]
          updated[idx] = pendingFilter.value
          visible = updated
        } else {
          visible = [pendingFilter.value, ...filters.value]
        }
      } else {
        visible = filters.value
      }

      const visibleWithBuckets = excludeBucketManagedFilters(visible)

      return visibleWithBuckets.filter((f) => !f.hidden)
    })

    const getAllFilters = computed(() => {
      let visible: JobFilter[]

      if (pendingFilter.value !== undefined) {
        const idx = filters.value.findIndex((f) => f.id === pendingFilter.value!.id)
        if (idx !== -1) {
          const updated = [...filters.value]
          updated[idx] = pendingFilter.value
          visible = updated
        } else {
          visible = [pendingFilter.value, ...filters.value]
        }
      } else {
        visible = filters.value
      }

      return excludeBucketManagedFilters(visible)
    })

    const getVisibleFilterById = computed(() => {
      return (id: string) => getVisibleFilters.value.find((filter) => filter.id === id)
    })

    function setPendingFilter(filter: JobFilter | undefined) {
      pendingFilter.value = filter
    }

    function setSelectedBucket(bucket: BucketDefinition | null) {
      selectedBucket.value = bucket
    }

    function removeFilter(id: string) {
      const index = filters.value.findIndex((filter) => filter.id === id)

      if (index !== -1) {
        filters.value.splice(index, 1)
      } else if (id === pendingFilter.value?.id) {
        pendingFilter.value = undefined
      }
    }

    function setFilter(filter: JobFilter) {
      pendingFilter.value = undefined

      const index = filters.value.findIndex((f) => f.id === filter.id)

      if (index !== -1) {
        const next = { ...filters.value[index], ...filter }
        filters.value.splice(index, 1, next)
      } else {
        filters.value.push(filter)
      }
    }

    function updatePage(page: number) {
      if (selectedBucket.value) {
        selectedBucket.value.currentPage = page
      } else {
        currentPage.value = page
      }
    }

    function updateItemsPerPage(items: ItemsPerPageOptions) {
      itemsPerPage.value = items
    }

    function loadFilterSet(filterSet: JobFilterSet) {
      const bucketDateFilters = filters.value
        .filter((filter) => isBucketDateFilter(filter))
        .map((filter) => ({ ...filter }))
      const bucketStatusFilters = filters.value
        .filter((filter) => isBucketStatusFilter(filter))
        .map((filter) => ({ ...filter }))

      pendingFilter.value = undefined
      selectedFilterSetId.value = filterSet.id

      const filtersFromSet = (filterSet.data.filters || [])
        .filter((filter: JobFilter) => !isBucketDateFilter(filter) && !isBucketStatusFilter(filter))
        .map((filter) => ({ ...filter }))

      filters.value = [...bucketDateFilters, ...bucketStatusFilters, ...filtersFromSet]
    }

    function setSelectedFilterSetId(id: string | null) {
      selectedFilterSetId.value = id
    }

    function clearFilters() {
      filters.value = filters.value.filter(
        (filter) => isBucketDateFilter(filter) || isBucketStatusFilter(filter),
      )
      pendingFilter.value = undefined
      selectedFilterSetId.value = null
    }

    const getItemsPerPage = computed(() => {
      return itemsPerPage.value ?? DEFAULT_ITEMS_PER_PAGE
    })

    const getCurrentPage = computed(() => {
      if (selectedBucket.value) {
        return selectedBucket.value.currentPage
      }
      return currentPage.value
    })

    return {
      // raw filters are exposed for persistence/normalization; prefer getFilters for consumers
      filters,
      getFilters,
      getFiltersWithoutBuckets: filtersWithoutBuckets,
      getPendingFilter: pendingFilter,
      getSelectedBucket: selectedBucket,
      getSelectedFilterSetId: selectedFilterSetId,
      setFilter,
      removeFilter,
      getSortForBucket,
      updateSorting,
      getSortForSelectedBucket,
      pendingFilter,
      setPendingFilter,
      setSelectedBucket,
      getCurrentPage,
      updatePage,
      itemsPerPage,
      getItemsPerPage,
      updateItemsPerPage,
      getVisibleFilters,
      getVisibleFilterById,
      getAllFilters,
      setSelectedFilterSetId,
      loadFilterSet,
      clearFilters,
      isBucketsSidePanelCollapsed,
      toggleBucketsSidePanel,
      getBucketSortSelections: bucketSortSelections,
      setSortForBucket,
      clearSortForBucket,
      activeDatePresetForBucketGroupMap,
    }
  },
  {
    persist: {
      beforeRestore: () => {
        return new Promise((resolve) => {
          requestAnimationFrame(resolve)
        })
      },
      afterRestore: (ctx) => {
        // TODO: Remove this legacy items-per-page migration once old bucket-scoped preferences are no longer expected in local storage.
        // Older persisted state stored this preference on the selected bucket only.
        // Newer persisted state stores one shared top-level itemsPerPage value.
        let persistedState: Record<string, unknown> | null = null
        if (typeof localStorage !== 'undefined') {
          const rawPersistedState = localStorage.getItem(ctx.store.$id)
          if (rawPersistedState) {
            try {
              const parsedPersistedState = JSON.parse(rawPersistedState)
              if (
                parsedPersistedState &&
                typeof parsedPersistedState === 'object' &&
                !Array.isArray(parsedPersistedState)
              ) {
                persistedState = parsedPersistedState
              }
            } catch {
              persistedState = null
            }
          }
        }
        const hasPersistedItemsPerPage = Object.prototype.hasOwnProperty.call(
          persistedState ?? {},
          'itemsPerPage',
        )
        ctx.store.getBucketSortSelections = normalizeBucketSortSelections(
          ctx.store.getBucketSortSelections,
          ctx.store.getSelectedBucket,
        )
        const legacySelectedBucketItemsPerPage = normalizeItemsPerPage(
          ctx.store.getSelectedBucket?.itemsPerPage,
        )
        ctx.store.itemsPerPage = normalizeItemsPerPage(
          hasPersistedItemsPerPage ? persistedState?.itemsPerPage : undefined,
          legacySelectedBucketItemsPerPage,
        )
        const filters = Array.isArray(ctx.store.filters) ? ctx.store.filters : []
        ctx.store.filters = normalizeAndEvictBucketDateFiltersInPlace(filters)
        ctx.store.activeDatePresetForBucketGroupMap = normalizeActiveDatePresetForBucketGroupMap(
          ctx.store.activeDatePresetForBucketGroupMap,
          ctx.store.filters,
        )

        // If the selected bucket's default date filter was just evicted
        // (defaults expire immediately), re-derive it so queries that fire
        // before JobsTableTopBar mounts already have the required date range.
        restoreEvictedDefaultDateFilter(ctx.store.getSelectedBucket, ctx.store.filters)
      },
    },
  },
)
